import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router-dom';
import { motion, AnimatePresence } from 'motion/react';
import { AlertCircle, Loader2, ShieldCheck, ArrowLeft, Sparkles } from 'lucide-react';
import { useSocialLogin, GOOGLE_NOT_CONFIGURED, type PendingSocialAuth } from '../hooks/useSocialLogin';
import { LocaleLink } from './i18n/exports';
import type { RegistrationConsents } from '../features/legal-consent/types';

/**
 * SocialLogin Component - Production-grade social authentication
 * Google (GSI) and Apple Sign-In with ID token flow
 * Calls POST /api/v1/auth/social and completes auth via AuthContext
 */

interface SocialLoginProps {
  onSuccess?: (user: unknown) => void;
  onError?: (error: string) => void;
  /** When provided (overlay mode), skip navigate and call this instead */
  onAuthenticated?: () => void;
  disabled?: boolean;
  className?: string;
}

/** Shared button style for both social providers -- identical height, font, weight, colors */
const socialBtnClass =
  'relative flex w-full items-center justify-center rounded border border-[#dadce0] bg-white text-[14px] font-normal text-[#5f6368] transition-colors duration-150';
/** Hover added via direct :hover for real buttons, group-hover for visual overlays */
const socialBtnHover = 'hover:border-[#d2e3fc] hover:bg-[#f8faff]';
const socialBtnGroupHover = 'group-hover:border-[#d2e3fc] group-hover:bg-[#f8faff]';

/** Google "G" multicolor logo -- matches official branding guidelines */
const GoogleLogo: React.FC<{ className?: string }> = ({ className }) => (
  <svg className={className} viewBox="0 0 24 24" fill="none">
    <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 01-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z" fill="#4285F4" />
    <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853" />
    <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05" />
    <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335" />
  </svg>
);

/** Apple logo */
const AppleLogo: React.FC<{ className?: string }> = ({ className }) => (
  <svg className={className} viewBox="0 0 24 24" fill="none">
    <path d="M18.71 19.5c-.83 1.24-1.71 2.45-3.05 2.47-1.34.03-1.77-.79-3.29-.79-1.53 0-2 .77-3.27.82-1.31.05-2.3-1.32-3.14-2.53C4.25 17 2.94 12.45 4.7 9.39c.87-1.52 2.43-2.48 4.12-2.51 1.28-.02 2.5.87 3.29.87.78 0 2.26-1.07 3.81-.91.65.03 2.47.26 3.64 1.98-.09.06-2.17 1.28-2.15 3.81.03 3.02 2.65 4.03 2.68 4.04-.03.07-.42 1.44-1.38 2.83M13 3.5c.73-.83 1.94-1.46 2.94-1.5.13 1.17-.34 2.35-1.04 3.19-.69.85-1.83 1.51-2.95 1.42-.15-1.15.41-2.35 1.05-3.11z" fill="#3c4043" />
  </svg>
);
/**
 * Reusable social button -- used for Apple (direct click) and
 * as the visual overlay for Google (clicks pass through to hidden GSI iframe).
 */
const SocialButton: React.FC<{
  onClick?: () => void;
  isLoading?: boolean;
  disabled?: boolean;
  icon: React.ReactNode;
  label: string;
  /** When true, renders as a visual-only overlay (pointer-events-none) */
  visualOnly?: boolean;
}> = ({ onClick, isLoading, disabled, icon, label, visualOnly }) => {
  const Tag = visualOnly ? 'div' : 'button';
  return (
    <Tag
      {...(!visualOnly && { type: 'button', onClick, disabled: disabled || isLoading })}
      style={{ height: 40 }}
      className={
        socialBtnClass + ' ' +
        (visualOnly
          ? 'pointer-events-none ' + socialBtnGroupHover
          : socialBtnHover + ' ' + (disabled || isLoading ? 'cursor-not-allowed opacity-50' : 'cursor-pointer'))
      }
      aria-label={!visualOnly ? label : undefined}
    >
      {isLoading ? (
        <Loader2 className="h-4 w-4 animate-spin text-gray-500" />
      ) : (
        <>
          <span className="absolute left-3">{icon}</span>
          <span>{label}</span>
        </>
      )}
    </Tag>
  );
};

/**
 * SocialLogin Component - Main component for social authentication
 * Google: GSI button rendered in container. Apple: custom button.
 */
export const SocialLogin: React.FC<SocialLoginProps> = ({
  onSuccess,
  onError,
  onAuthenticated,
  disabled = false,
  className = '',
}) => {
  const { t } = useTranslation('auth');
  const location = useLocation();
  const cameFromOneTap = !!(location.state as { pendingSocialAuth?: PendingSocialAuth } | null)?.pendingSocialAuth;
  const googleContainerRef = useRef<HTMLDivElement>(null);
  const {
    handleAppleLogin,
    renderGoogleSignInButton,
    isLoading,
    error,
    currentProvider,
    pendingSocialAuth,
    completeSocialAuth,
    cancelSocialAuth,
    clearError,
  } = useSocialLogin({
    onSuccess: user => onSuccess?.(user),
    onAuthenticated,
  });

  // Consent state for the inline consent step
  const [consentTerms, setConsentTerms] = useState(false);
  const [consentPrivacy, setConsentPrivacy] = useState(false);
  const [consentMarketing, setConsentMarketing] = useState(false);
  const isConsentReady = consentTerms && consentPrivacy;

  // Reset consent checkboxes when pending auth changes
  useEffect(() => {
    if (!pendingSocialAuth) {
      setConsentTerms(false);
      setConsentPrivacy(false);
      setConsentMarketing(false);
    }
  }, [pendingSocialAuth]);

  useEffect(() => {
    if (!googleContainerRef.current || disabled || pendingSocialAuth) return;
    renderGoogleSignInButton(googleContainerRef.current);
  }, [renderGoogleSignInButton, disabled, pendingSocialAuth]);

  const handleAppleClick = async (): Promise<void> => {
    clearError();
    const result = await handleAppleLogin();
    if (result.error) {
      onError?.(result.error);
    }
  };

  const handleConsentConfirm = async (): Promise<void> => {
    if (!isConsentReady) return;
    const consents: RegistrationConsents = {
      termsOfService: true,
      privacyPolicy: true,
      marketingEmail: consentMarketing,
    };
    const result = await completeSocialAuth(consents);
    if (result.success && result.user) {
      onSuccess?.(result.user);
    } else if (result.error) {
      onError?.(result.error);
    }
  };

  // When consent is pending, show inline consent checkboxes instead of social buttons
  if (pendingSocialAuth) {
    const providerName = pendingSocialAuth.provider === 'google' ? 'Google' : 'Apple';
    return (
      <motion.div
        initial={{ opacity: 0, y: 8 }}
        animate={{ opacity: 1, y: 0 }}
        className={`space-y-4 ${className}`}
      >
        {/* Welcome banner -- shown when user arrives from One Tap on another page */}
        {cameFromOneTap && (
          <div className="flex items-start gap-2.5 rounded-xl bg-emerald-50/80 border border-emerald-100 p-3">
            <Sparkles className="h-4 w-4 text-emerald-500 shrink-0 mt-0.5" />
            <div>
              <p className="text-sm font-medium text-emerald-800">
                {t('multiMethod.oneTapWelcome', 'Welcome! One more step to create your account.')}
              </p>
              <p className="mt-0.5 text-xs text-emerald-600/80">
                {t('multiMethod.oneTapWelcomeSubtitle', 'Please review the following to complete sign-up.')}
              </p>
            </div>
          </div>
        )}

        {/* Consent header */}
        <div className="flex items-center gap-2 text-center">
          <ShieldCheck className="h-4 w-4 text-emerald-600 shrink-0" />
          <p className="text-sm font-medium text-gray-800">
            {t('multiMethod.beforeCompletingSignIn', { provider: providerName })}
          </p>
        </div>

        {/* Consent checkboxes */}
        <div className="space-y-3 rounded-xl border border-gray-100 bg-gray-50/50 p-4">
          <label className="flex items-start gap-3 cursor-pointer group" htmlFor="social-tos">
            <input
              id="social-tos"
              type="checkbox"
              checked={consentTerms}
              onChange={e => setConsentTerms(e.target.checked)}
              className="mt-0.5 h-4 w-4 rounded border-gray-300 text-emerald-600 focus:ring-emerald-500 focus:ring-offset-1"
            />
            <span className="text-sm text-gray-700 group-hover:text-gray-900 transition-colors">
              {t('multiMethod.agreeToTerms')}{' '}
              <LocaleLink to="/terms" target="_blank" rel="noopener noreferrer" className="text-emerald-600 hover:text-emerald-700 underline font-medium">
                {t('multiMethod.termsOfService')}
              </LocaleLink>
            </span>
          </label>

          <label className="flex items-start gap-3 cursor-pointer group" htmlFor="social-privacy">
            <input
              id="social-privacy"
              type="checkbox"
              checked={consentPrivacy}
              onChange={e => setConsentPrivacy(e.target.checked)}
              className="mt-0.5 h-4 w-4 rounded border-gray-300 text-emerald-600 focus:ring-emerald-500 focus:ring-offset-1"
            />
            <span className="text-sm text-gray-700 group-hover:text-gray-900 transition-colors">
              {t('multiMethod.acknowledgePrivacy')}{' '}
              <LocaleLink to="/privacy" target="_blank" rel="noopener noreferrer" className="text-emerald-600 hover:text-emerald-700 underline font-medium">
                {t('multiMethod.privacyPolicy')}
              </LocaleLink>
            </span>
          </label>

          <label className="flex items-start gap-3 cursor-pointer group" htmlFor="social-marketing">
            <input
              id="social-marketing"
              type="checkbox"
              checked={consentMarketing}
              onChange={e => setConsentMarketing(e.target.checked)}
              className="mt-0.5 h-4 w-4 rounded border-gray-300 text-emerald-600 focus:ring-emerald-500 focus:ring-offset-1"
            />
            <span className="text-xs text-gray-500 group-hover:text-gray-700 transition-colors">
              {t('multiMethod.marketingConsent')}
            </span>
          </label>
        </div>

        {/* Action buttons */}
        <div className="flex gap-3">
          <button
            type="button"
            onClick={cancelSocialAuth}
            className="flex items-center justify-center gap-1.5 rounded-xl border border-gray-200 bg-white px-4 py-2.5 text-sm font-medium text-gray-600 transition-colors hover:bg-gray-50"
          >
            <ArrowLeft className="h-3.5 w-3.5" />
            {t('multiMethod.consentBack')}
          </button>
          <button
            type="button"
            onClick={handleConsentConfirm}
            disabled={!isConsentReady || isLoading}
            className={
              'flex-1 rounded-xl px-4 py-2.5 text-sm font-semibold transition-all duration-200 ' +
              (isConsentReady && !isLoading
                ? 'bg-emerald-600 text-white shadow-md shadow-emerald-600/20 hover:bg-emerald-500'
                : 'cursor-not-allowed bg-gray-100 text-gray-400')
            }
          >
            {isLoading ? (
              <span className="flex items-center justify-center gap-2">
                <Loader2 className="h-4 w-4 animate-spin" />
                {t('multiMethod.signingIn')}
              </span>
            ) : (
              t('multiMethod.continueWithProvider', { provider: providerName })
            )}
          </button>
        </div>

        {/* Error display */}
        <AnimatePresence>
          {error && (
            <motion.div
              initial={{ opacity: 0, y: -10 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -10 }}
              className="flex items-start space-x-2 p-3 bg-red-50 border border-red-200 rounded-lg"
            >
              <AlertCircle className="w-4 h-4 text-red-500 shrink-0 mt-0.5" />
              <p className="text-xs text-red-600 flex-1">{error}</p>
              <button onClick={clearError} className="text-red-500 hover:text-red-700" aria-label="Dismiss">×</button>
            </motion.div>
          )}
        </AnimatePresence>
      </motion.div>
    );
  }

  return (
    <div className={`space-y-3 ${className}`}>
      {/* Social buttons -- identical custom styling for both */}
      <div className="mx-auto w-full max-w-[400px] space-y-3">
        {/* Google -- invisible GSI iframe receives clicks, custom button shows visually */}
        <div className="group relative cursor-pointer" style={{ height: 40 }}>
          <div
            ref={googleContainerRef}
            className="absolute inset-0 z-10 opacity-0"
            aria-hidden="true"
          />
          <SocialButton
            visualOnly
            icon={<GoogleLogo className="h-[18px] w-[18px]" />}
            label={t('multiMethod.continueWithGoogle', 'Continue with Google')}
          />
        </div>

        {/* Apple -- direct click handler */}
        <SocialButton
          onClick={handleAppleClick}
          isLoading={isLoading && currentProvider === 'apple'}
          disabled={disabled || isLoading}
          icon={<AppleLogo className="h-5 w-5" />}
          label={t('multiMethod.continueWithApple', 'Continue with Apple')}
        />
      </div>

      {/* Error Display */}
      <AnimatePresence>
        {error && (
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            className="flex items-start space-x-2 p-3 bg-red-50 border border-red-200 rounded-lg"
          >
            <AlertCircle className="w-4 h-4 text-red-500 shrink-0 mt-0.5" />
            <div className="flex-1">
              <p className="text-sm text-red-700 font-medium">{t('multiMethod.loginError')}</p>
              <p className="text-xs text-red-600 mt-1">
                {error === GOOGLE_NOT_CONFIGURED ? t('multiMethod.googleNotConfigured') : error}
              </p>
            </div>
            <button
              onClick={clearError}
              className="text-red-500 hover:text-red-700 transition-colors"
              aria-label={t('multiMethod.dismissError')}
            >
              ×
            </button>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Loading State Overlay */}
      <AnimatePresence>
        {isLoading && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/20 backdrop-blur-sm flex items-center justify-center z-50"
          >
            <motion.div
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              className="bg-white rounded-lg p-6 shadow-xl max-w-sm mx-4"
            >
              <div className="flex items-center space-x-3">
                <Loader2 className="w-5 h-5 animate-spin text-emerald-500" />
                <div>
                  <p className="font-medium text-gray-900">
                    {t('multiMethod.connectingTo', {
                      provider: currentProvider
                        ? currentProvider.charAt(0).toUpperCase() + currentProvider.slice(1)
                        : ''
                    })}
                  </p>
                  <p className="text-sm text-gray-600">{t('multiMethod.pleaseWait')}</p>
                </div>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

/**
 * SocialLoginSection - Wrapper component for integration into forms
 * Provides consistent styling and layout
 */
export const SocialLoginSection: React.FC<SocialLoginProps & {
  title?: string;
  subtitle?: string;
}> = ({
  title = '',
  subtitle,
  ...props
}) => {
  return (
    <div className="mt-6 space-y-3">
      {/* Subtle divider line */}
      <div className="border-t border-gray-100" />

      {/* Section Header - only render when title provided */}
      {title && (
        <div className="text-center">
          <h3 className="text-sm font-semibold text-gray-700 mb-2">{title}</h3>
          {subtitle && (
            <p className="text-xs text-gray-500">{subtitle}</p>
          )}
        </div>
      )}

      {/* Social Login Component */}
      <SocialLogin {...props} />
    </div>
  );
};

export default SocialLogin;
